import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { bidService } from '../services/bidService';
import { reportService } from '../services/reportService';
import type { RootState } from '../store';
import { BidStatus } from '../types';
import type { BidSummaryResponse } from '../types';

export function ReportsPage() {
  const user = useSelector((s: RootState) => s.auth.user);
  const { data: bids, isLoading } = useQuery({ queryKey: ['bids'], queryFn: bidService.getBids });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [report, setReport] = useState<(BidSummaryResponse & { generatedAt?: string }) | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mineEnded = (bids ?? []).filter(
    (b) => b.status === BidStatus.ENDED && b.creatorId === user?.id && user?.role === 'PROCUREMENT'
  );

  const loadExisting = async (bidId: string) => {
    setLoading(true);
    setError(null);
    setSelectedId(bidId);
    try {
      const r = await reportService.get(bidId);
      setReport(r);
    } catch {
      setReport(null);
      setError('No report yet. Generate one below.');
    } finally {
      setLoading(false);
    }
  };

  const generate = async (bidId: string) => {
    setLoading(true);
    setError(null);
    setSelectedId(bidId);
    try {
      const r = await reportService.generate(bidId);
      setReport({ ...r, generatedAt: new Date().toISOString() });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to generate');
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== 'PROCUREMENT') {
    return (
      <div>
        <h1 className="text-xl font-black text-slate-900 mb-4">Reports</h1>
        <p className="text-slate-500 text-sm">AI bid reports are available for procurement creators on ended auctions.</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-xl font-black text-slate-900 mb-4">Reports</h1>
      <p className="text-slate-500 text-sm mb-6">Select an ended bid you created to view or generate an AI summary.</p>

      {isLoading ? (
        <p className="text-slate-500">Loading bids...</p>
      ) : mineEnded.length === 0 ? (
        <p className="text-slate-400 text-sm italic">No ended bids to report yet.</p>
      ) : (
        <ul className="space-y-2 mb-8">
          {mineEnded.map((b) => (
            <li
              key={b.id}
              className="flex flex-wrap gap-2 items-center justify-between bg-white border border-slate-100 rounded-xl p-4"
            >
              <span className="font-bold text-slate-800">{b.title}</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => loadExisting(b.id)}
                  className="text-xs font-bold bg-slate-100 px-3 py-2 rounded-lg"
                >
                  Load saved
                </button>
                <button
                  type="button"
                  onClick={() => generate(b.id)}
                  className="text-xs font-bold bg-blue-600 text-white px-3 py-2 rounded-lg"
                >
                  Generate
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {error && <p className="text-red-600 text-sm mb-4">{error}</p>}
      {loading && <p className="text-slate-500 text-sm mb-4">Working...</p>}

      {report && selectedId && (
        <div className="bg-white border border-slate-100 rounded-2xl p-6 space-y-4">
          <h2 className="font-black text-lg text-slate-900">Summary</h2>
          <p className="text-slate-600 text-sm whitespace-pre-wrap">{report.summary}</p>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-400 text-[10px] font-bold uppercase">Total savings</p>
              <p className="font-black text-emerald-600">{report.totalSavings}</p>
            </div>
            <div>
              <p className="text-slate-400 text-[10px] font-bold uppercase">% saved</p>
              <p className="font-black">{report.percentageSaved}</p>
            </div>
          </div>
          <div>
            <p className="text-slate-400 text-[10px] font-bold uppercase mb-1">Recommendation</p>
            <p className="text-slate-700 text-sm">{report.recommendation}</p>
          </div>
          {report.generatedAt && (
            <p className="text-[10px] text-slate-400">Generated: {new Date(report.generatedAt).toLocaleString()}</p>
          )}
        </div>
      )}
    </div>
  );
}
